import React from 'react';
import { NavLink, match } from 'react-router-dom';
import { MIN_DESKTOP_LABEL_LENGTH } from '../../../hooks/use-navbar-items';
import { SESSION_MEETING_PATH_REGEX } from '../../../lib/constants';
import {
  BOOK_A_MEETING_SOURCE,
  BOOK_A_MEETING_SOURCE_PATH_PARAM
} from '../../../lib/helpers/bookAMeetingNavigationHelper';

export interface IProps extends Partial<NavLink> {
  className?: string,
  activeClassName?: string,
  onClick?(): void,
  label: string,
  to: string,
  reload?: boolean,
  isMobile: boolean,
  exact?: boolean,
}

const className = 'my-0 px-4 sm:px-4 whitespace-no-wrap font-size-14px font-medium cursor-pointer flex justify-end navbar-item-label';
const activeClassName = 'navbar-item-label-active';

export const NavItem: React.FC<IProps> = (props: IProps): JSX.Element | null => {
  const { label, isMobile, reload, onClick, to, ...rest } = props;

  const style = { minWidth: '10ch' };
  const _props = label.length > MIN_DESKTOP_LABEL_LENGTH ? { ...rest, style } : rest;

  const reloadPage = () => {
    if (reload) {
      const { pathname, origin } = window.location;
      /* [OEC-6073] reloading the page only if the user is already on that page */
      if (pathname === props.to) window.location.assign(origin + props.to);
      if (isMobile && onClick) onClick();
    }
  };

  /**
   * Custom isActive function to handle Book a Meeting navigation context.
   * Ensures Book a Meeting tab stays highlighted when viewing meeting details navigated from Book a Meeting
   * and prevents other tabs (like General Sessions) from being highlighted when history state points to Book a Meeting.
   */
  const isActive = (match: match | null, location: { pathname: string; state?: unknown; search?: string }): boolean => {
    // Check if we're on a meeting details page
    const isMeetingDetailsPage = SESSION_MEETING_PATH_REGEX.test(location.pathname);

    // If we're on a meeting details page
    if (isMeetingDetailsPage) {
      // Check query parameter first (for calendar invite links with source=book-a-meeting)
      // This prevents the General Sessions tab from flashing before history state is set
      if (location.search) {
        const searchParams = new URLSearchParams(location.search);
        const source = searchParams.get('source');
        if (source === BOOK_A_MEETING_SOURCE) {
          if (typeof location.state === 'string') {
            return location.state === to;
          }

          const sourcePath = searchParams.get(BOOK_A_MEETING_SOURCE_PATH_PARAM);
          if (sourcePath) {
            const slug = sourcePath.replace(/^\//, '');
            return to === `/${slug}` || to.endsWith(`/${slug}`);
          }

          // Wait for MeetingWrapper to set history state (avoids wrong tab for custom slugs)
          return false;
        }
      }

      // Check history state if available (string origin path from Join or calendar resolution)
      if (typeof location.state === 'string') {
        // Only this nav item should be active if history state matches its 'to' prop
        // This prevents other nav items (like General Sessions) from being highlighted
        return location.state === to;
      }

      // If no state and no source parameter, prevent default matching for meeting details pages
      // This prevents General Sessions from being highlighted before history state is set
      return false;
    }

    // For non-meeting-details pages, use default matching behavior
    if (match) {
      return true;
    }

    return false;
  };

  const navLabel = isMobile
    ? <span className='truncate leading-4 my-4'>{label}</span>
    : <span className='truncate w-full'>{label}</span>
  ;

  return (
    <NavLink 
      {..._props} 
      to={to}
      isActive={isActive}
      onClick={reload ? reloadPage : (isMobile ? onClick : undefined)}
    >
      {navLabel}
    </NavLink>
  );
};

NavItem.defaultProps = {
  className,
  activeClassName
};
